import { useMemo } from 'react';
import { NativeSyntheticEvent, TextInputFocusEventData } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { IFloatingFieldProps } from '@/common/components/Fields/FloatingField/FloatingField';
import { SelectField } from '@/common/components/Fields/SelectField/SelectField';
import { Stack } from '@/common/components/Stack/Stack';
import { COUNTRIES, ICountry } from '@coverflex-tech/hypernova-utils';

export type TCountryItem = ICountry & {
  label: string;
  value: string;
};

export interface TCountryFieldProps
  extends Pick<IFloatingFieldProps, 'hint' | 'disabled'> {
  id: string;
  disabled?: boolean;
  label: string;
  value?: TCountryItem;
  onChange: (value?: TCountryItem) => void;
  onBlur?: (evt: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  className?: string;
  allowClear?: boolean;
  error?: string;
  suggestedCountries?: string[];
}

export function CountryField({
  id,
  label,
  value,
  error,
  hint,
  onChange,
  onBlur,
  disabled,
  className,
  suggestedCountries,
}: TCountryFieldProps) {
  const countriesList = useMemo(() => {
    const topCountries = COUNTRIES.filter((c) =>
      suggestedCountries?.includes(c.iso2)
    );
    return topCountries.concat(COUNTRIES).map(mapCountryToItem);
  }, [suggestedCountries]);

  const country = useMemo(() => {
    return countriesList.find((c) => c.value === value?.iso2);
  }, [value?.iso2, countriesList]);

  return (
    <SelectField
      className={className}
      id={id}
      label={label}
      options={countriesList}
      disabled={disabled}
      value={country}
      onChange={onChange}
      onBlur={onBlur}
      renderItem={renderCountryItem}
      error={error}
      hint={hint}
    />
  );
}

function mapCountryToItem(country: ICountry): TCountryItem {
  return {
    ...country,
    label: country.name,
    value: country.iso2,
  };
}

function renderCountryItem(item: TCountryItem) {
  return (
    <Stack horizontal align="center" grow gap="sm">
      <BodyText>{item.flag}</BodyText>
      <BodyText bold>{item.name}</BodyText>
    </Stack>
  );
}
